import React, { useCallback, useState } from 'react';
import { useIntl } from 'react-intl';
import { defineMessages } from 'react-intl.macro';

import { createStyles, Theme, withStyles, WithStyles } from '@material-ui/core';
import { flatten, get } from 'lodash';
import moment from 'moment';
import { TAppointment, useAppointmentManyV2 } from 'ui-model';

import { useCurrentUser } from '@lib/hooks';
import { PatientFormTemplateItemTypeKeys } from '@lib/resources/types';

import { Button, Typography, TypographyI18n } from 'components/v2';
import { generateMessageTemplateKey } from 'hooks';

import { useLumaBotGetTemplateText } from 'modules/LumaBotV2/useLumaBotGetTemplateText';
import { ChatContextProps, useChatContext } from '../../../contexts/ChatContext';
import { getLumaBotErrorMessageHandler } from '../../../utils';
import ChatMessage, { ChatMessageProps } from '../ChatMessage';
import { TMessageWrapper } from '../MessageWrapper';

import { ListAppointments } from './components';
import { usePostLumaBotReschedulerReminder } from './queries';

const messages = defineMessages({
  upcomingApptTitle: {
    id: 'modules.LumaBot.components.ManageAppointments.ManageAppointments.upcomingApptTitle',
    defaultMessage: 'Upcoming Appointments',
  },
});

const styles = ({ spacing: { unit } }: Theme) =>
  createStyles({
    choicesWrapper: {
      width: '100%',
      maxWidth: 264,
      boxSizing: 'border-box',
      height: 'fit-content',
      padding: `${unit * 1.5}px ${unit * 2.5}px ${unit * 2.5}px`,
      backgroundColor: '#fff',
      boxShadow: '0px 0px 8px rgba(0, 0, 0, 0.16)',
      borderRadius: '8px',
      marginBottom: unit * 2,
      marginLeft: unit * 4,
    },
  });

interface TManageAppointmentsProps extends Omit<ChatMessageProps, 'classes' | 'message'>, WithStyles<typeof styles> {
  message: TMessageWrapper;
  onSubmit: ChatContextProps['pushAnswer'];
  currentDate: Date;
}

const ManageAppointments = ({ classes, message, onSubmit, currentDate, ...rest }: TManageAppointmentsProps) => {
  const successMessage = useLumaBotGetTemplateText('patientLumaBotManageAppointmentSuccess');
  const doneMessage = useLumaBotGetTemplateText('patientLumaBotManageAppointmentDone');
  const { formatMessage } = useIntl();
  const { flowTemplates } = useChatContext();
  const currentUser = useCurrentUser();
  const [selectedAppointment, setSelectedAppointment] = useState<TAppointment>();
  const [isRescheduleLoading, setRescheduleLoading] = useState(false);
  const [isRescheduleDisabled, setRescheduleDisabled] = useState(false);
  const [listOfUpcomingAppointments, setListOfUpcomingAppointments] = useState<TAppointment[]>([]);

  const { mutateAsync: postReminder } = usePostLumaBotReschedulerReminder();

  const { isLoading: isLoadingUpcomingAppointments } = useAppointmentManyV2(
    {
      patient: currentUser?.id,
      date: `>=${currentDate.toISOString()}`,
      status: 'confirmed,unconfirmed',
      _populate: 'facility.name,facility.address,facility.phone,type.name,type.modality,provider.name',
      limit: 10,
    },
    {
      onSuccess: (response) => {
        const flattenResponse = flatten(get(response, 'pages', []));
        setSelectedAppointment(flattenResponse[0]);
        if (flattenResponse.length > 0) setListOfUpcomingAppointments(flattenResponse);
      },
      enabled: !isRescheduleDisabled,
    },
  );

  const handleSuccess = useCallback(() => {
    onSubmit({
      text: successMessage,
      type: 'inbound',
    });
  }, [onSubmit, successMessage]);

  const handleError = useCallback(() => {
    const itemType: PatientFormTemplateItemTypeKeys = get(message, 'metadata.item.type');
    const item = getLumaBotErrorMessageHandler(flowTemplates, itemType);

    onSubmit({ text: 'force', type: 'inbound', metadata: { item } }, { skipNextItem: true });
  }, [message, flowTemplates, onSubmit]);

  // TODO: useCallback
  const handleOnSubmit = (appointment: TAppointment) => {
    setRescheduleLoading(true);
    setRescheduleDisabled(true);
    onSubmit(
      {
        text: `Appt. at ${moment(appointment.date).format('MM/DD/YYYY hh:mm A')}`,
        type: 'outbound',
        metadata: { ...message.metadata, answer: appointment },
      },
      { skipNextItem: true },
    );

    postReminder(
      {
        appointment: appointment._id,
      },
      {
        onSettled: () => setRescheduleLoading(false),
        onSuccess: handleSuccess,
        onError: handleError,
      },
    );
  };

  // TODO: useCallback
  const handleLeave = () => {
    setRescheduleDisabled(true);
    onSubmit({
      text: doneMessage,
      type: 'outbound',
      metadata: { ...message.metadata },
    });
  };

  return (
    <>
      <ChatMessage message={message} {...rest} />
      <div className={classes.choicesWrapper}>
        <Typography variant="h3W600F17" align="center">
          {formatMessage(messages.upcomingApptTitle)}
        </Typography>
        <ListAppointments
          appointments={listOfUpcomingAppointments}
          selectedAppointment={selectedAppointment}
          handleAppointmentSelect={setSelectedAppointment}
          isLoading={isLoadingUpcomingAppointments}
          isDisabled={isRescheduleDisabled}
          onSubmit={handleOnSubmit}
          isRescheduleLoading={isRescheduleLoading}
        />
        <Button
          data-lh-id="button-leave"
          color="primary"
          variant="contained"
          onClick={handleLeave}
          disabled={isRescheduleDisabled}
          fullWidth
          extraRounded
          extraPadding
        >
          <TypographyI18n variant="bW600F14" color="inherit">
            {generateMessageTemplateKey('patientLumaBotManageAppointmentDone')}
          </TypographyI18n>
        </Button>
      </div>
    </>
  );
};

export default withStyles(styles, { name: 'ManageAppointments' })(ManageAppointments);
